<script>
import axios from '@/utils/axios';
import Swal from 'sweetalert2';

export default {
  inject: ['setLoading'],
  data() {
    return {
      item_id: null,
      item_option_id: null,
      option_name: '',
      valuesList: [],
      loading: false,
      editing_id: null,
      form: { value: '', price_delta: 0, price_multiplier: 1, sort_order: 0 },
    };
  },
  methods: {
    async open(option, item_id) {
      this.item_id = item_id;
      this.item_option_id = option.id;
      this.option_name = option.name;
      this.valuesList = option.values || [];
      this.resetForm();
    },
    resetForm() {
      this.editing_id = null;
      this.form = { value: '', price_delta: 0, price_multiplier: 1, sort_order: 0 };
    },
    async refreshValues() {
      if (!this.item_id) {
        this.$swalToast('Missing item reference — please reopen this option.', 'error');
        return;
      }
      this.loading = true;
      try {
        const res = await axios.get(`/item-options/${this.item_id}`);
        const options = res.data.data || [];
        const match = options.find(o => o.id === this.item_option_id);
        this.valuesList = match?.values || [];
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.loading = false;
      }
    },
    editValue(row) {
      this.editing_id = row.id;
      this.form = {
        value: row.value,
        price_delta: row.price_delta,
        price_multiplier: row.price_multiplier,
        sort_order: row.sort_order,
      };
    },
    async saveValue() {
      if (!this.form.value?.trim()) {
        this.$swalToast('Value is required', 'warning');
        return;
      }
      this.setLoading(true);
      try {
        if (this.editing_id) {
          await axios.put(`/item-option-values/${this.editing_id}`, this.form);
          this.$swalToast('Value updated');
        } else {
          await axios.post(`/item-option-values/${this.item_option_id}`, { values: [this.form] });
          this.$swalToast('Value added');
        }
        this.resetForm();
        await this.refreshValues();
        this.$emit('updated');
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },
    async deleteValue(row) {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: `Delete "${row.value}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it',
      });
      if (!result.isConfirmed) return;
      this.setLoading(true);
      try {
        await axios.delete(`/item-option-values/${row.id}`);
        this.$swalToast('Value deleted');
        await this.refreshValues();
        this.$emit('updated');
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },
  },
};
</script>

<template>
  <div class="modal fade" id="item_option_value_modal" tabindex="-1">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content iov-modal">

        <div class="modal-header border-0 pb-0">
          <div>
            <h5 class="modal-title fw-bold mb-0">Manage Values</h5>
            <p class="iov-subtitle mb-0">
              Option: <span class="fw-semibold">{{ option_name }}</span>
              <span class="iov-count-chip" v-if="valuesList.length">{{ valuesList.length }}</span>
            </p>
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body pt-3">

          <!-- Values list -->
          <div class="iov-list mb-3">
            <div v-if="!valuesList.length" class="iov-empty">
              <i class="ti ti-list-details"></i>
              <p class="mb-0">No values added yet</p>
              <span>Use the form below to add the first one</span>
            </div>

            <template v-else>
              <div class="iov-row iov-row-head">
                <div class="iov-col iov-col-value">Value</div>
                <div class="iov-col iov-col-price">Price Effect</div>
                <div class="iov-col iov-col-sort">Sort</div>
                <div class="iov-col iov-col-action text-end">Action</div>
              </div>

              <div class="iov-scroll">
                <div v-for="row in valuesList" :key="row.id"
                  class="iov-row" :class="{ 'iov-row-editing': editing_id === row.id }">
                  <div class="iov-col iov-col-value">
                    <span class="iov-value-pill">{{ row.value }}</span>
                  </div>
                  <div class="iov-col iov-col-price">
                    <span class="badge iov-badge-delta" v-if="Number(row.price_delta)">
                      {{ Number(row.price_delta) >= 0 ? '+' : '' }}{{ row.price_delta }}
                    </span>
                    <span class="badge iov-badge-mult" v-if="Number(row.price_multiplier) !== 1">
                      ×{{ row.price_multiplier }}
                    </span>
                    <span class="iov-muted" v-if="!Number(row.price_delta) && Number(row.price_multiplier) === 1">
                      No change
                    </span>
                  </div>
                  <div class="iov-col iov-col-sort">
                    <span class="iov-muted">{{ row.sort_order }}</span>
                  </div>
                  <div class="iov-col iov-col-action text-end">
                    <button type="button" class="iov-icon-btn" @click="editValue(row)" title="Edit">
                      <i class="ti ti-edit"></i>
                    </button>
                    <button type="button" class="iov-icon-btn iov-icon-btn-danger" @click="deleteValue(row)" title="Delete">
                      <i class="ti ti-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <!-- Add / Edit form -->
          <div class="iov-form-card" :class="{ 'iov-form-card-editing': editing_id }">
            <div class="iov-form-title">
              <span class="d-flex align-items-center gap-2">
                <i class="ti" :class="editing_id ? 'ti-edit' : 'ti-circle-plus'"></i>
                {{ editing_id ? 'Edit Value' : 'Add New Value' }}
              </span>
              <button v-if="editing_id" type="button" class="iov-cancel-link" @click="resetForm">
                Cancel
              </button>
            </div>

            <div class="iov-form-grid">
              <div class="iov-field iov-field-value">
                <label class="form-label iov-label">Value</label>
                <input type="text" class="form-control" v-model="form.value" placeholder="e.g. XL, Red" />
              </div>
              <div class="iov-field">
                <label class="form-label iov-label">Price Delta</label>
                <input type="number" step="0.01" class="form-control" v-model.number="form.price_delta" />
              </div>
              <!-- <div class="iov-field">
                <label class="form-label iov-label">Multiplier</label>
                <input type="number" step="0.001" class="form-control" v-model.number="form.price_multiplier" />
              </div> -->
              <div class="iov-field">
                <label class="form-label iov-label">Sort Order</label>
                <input type="number" class="form-control" v-model.number="form.sort_order" />
              </div>
              <div class="iov-field iov-field-submit">
                <button type="button" class="btn btn-primary w-100" @click="saveValue">
                  {{ editing_id ? 'Update' : 'Add' }}
                </button>
              </div>
            </div>
          </div>

        </div>

        <div class="modal-footer border-0 pt-0">
          <button class="btn btn-light" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.iov-modal {
  border-radius: 16px;
  border: none;
  box-shadow: 0 20px 60px rgba(15, 23, 42, .18);
}

.modal-body {
  overflow-x: hidden;
}

.iov-subtitle {
  font-size: 12.5px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 8px;
}

.iov-count-chip {
  background: #eef2ff;
  color: #4f46e5;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 20px;
}

.iov-empty {
  text-align: center;
  padding: 32px 16px;
  border: 1.5px dashed #e2e8f0;
  border-radius: 12px;
  color: #94a3b8;
}
.iov-empty i {
  font-size: 28px;
  display: block;
  margin-bottom: 8px;
  color: #cbd5e1;
}
.iov-empty p {
  font-size: 13.5px;
  font-weight: 600;
  color: #64748b;
}
.iov-empty span {
  font-size: 12px;
}

.iov-list {
  border: 1px solid #eef2f7;
  border-radius: 12px;
  overflow: hidden;
}

.iov-scroll {
  max-height: 260px;
  overflow-y: auto;
  overflow-x: hidden;
}
.iov-scroll::-webkit-scrollbar {
  width: 6px;
}
.iov-scroll::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 10px;
}

.iov-row {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #f1f5f9;
  transition: background .12s;
}
.iov-scroll .iov-row:last-child {
  border-bottom: none;
}
.iov-row:not(.iov-row-head):hover {
  background: #f8fafc;
}
.iov-row-head {
  background: #f8fafc;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #94a3b8;
  padding-top: 10px;
  padding-bottom: 10px;
  position: sticky;
  top: 0;
  z-index: 1;
}
.iov-row-editing {
  background: #eef2ff;
}

.iov-col-value   { flex: 1 1 28%; min-width: 0; }
.iov-col-price   { flex: 1 1 34%; display: flex; gap: 6px; align-items: center; flex-wrap: wrap; min-width: 0; }
.iov-col-sort    { flex: 0 0 15%; }
.iov-col-action  { flex: 0 0 23%; white-space: nowrap; }

.iov-value-pill {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.iov-muted {
  font-size: 12px;
  color: #94a3b8;
}

.iov-badge-delta {
  background: #ecfdf5;
  color: #059669;
  border: 1px solid #a7f3d0;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 20px;
}
.iov-badge-mult {
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 20px;
}

.iov-icon-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  transition: all .12s;
}
.iov-icon-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}
.iov-icon-btn-danger:hover {
  background: #fef2f2;
  border-color: #fecaca;
  color: #ef4444;
}

.iov-form-card {
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 16px;
  transition: border-color .15s, background .15s;
}
.iov-form-card-editing {
  background: #eef2ff;
  border-color: #c7d2fe;
}

.iov-form-title {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.iov-form-title i {
  color: #6366f1;
}
.iov-cancel-link {
  border: none;
  background: none;
  color: #6366f1;
  font-size: 12px;
  font-weight: 600;
  padding: 0;
}
.iov-cancel-link:hover {
  text-decoration: underline;
}

.iov-form-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  gap: 12px;
  align-items: end;
}
.iov-field {
  min-width: 0;
}
.iov-field-submit {
  align-self: end;
}

.iov-label {
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
  display: block;
}

@media (max-width: 767px) {
  .iov-form-grid {
    grid-template-columns: 1fr 1fr;
  }
  .iov-field-value {
    grid-column: 1 / -1;
  }
  .iov-field-submit {
    grid-column: 1 / -1;
  }
}
</style>